"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Versification_1 = require("./Versification");
const P = require("parsimmon");
let book_name_to_id = {};
for (let book of Versification_1.default.order) {
    [book.name, ...book.aliases].map(x => book_name_to_id[x.toLowerCase()] = book.id);
}
///////////////////////////////////////////////////////////////////////
// Basic Parses
// differs to parsimons whitespace in that it doesn't consume new lines
const pAnySpace = P.regex(/[ \t]*/);
const pInt = P
    .regex(/[0-9]+/)
    .skip(pAnySpace)
    .map(s => Number(s));
///////////////////////////////////////////////////////////////////////
// Bible Book
const pBookId = P
    .regex(/([0-9A-Z]{3})\.?/i)
    .chain(x => {
    x = x.toUpperCase().substring(0, 3);
    if (Versification_1.default.book[x]) {
        return P.succeed(x);
    }
    else {
        return P.fail(`Invalid book ID: ${x}`);
    }
}).desc("USFM book identifier (eg: 'GEN', 'REV')");
const pBookPrefixNumber = P.alt(P.string("1st").map((x) => 1), P.string("2nd").map((x) => 2), P.string("3rd").map((x) => 3), P.oneOf("123").map(x => parseInt(x)), P.oneOf("I").times(1, 3).map(x => x.length));
const pBookName = P.alt(
// Multiword book names
P.regexp(/[Ss]ong\sof\s[Ss](ongs|olomon|ol)/).chain(x => P.succeed("SNG")), 
// Number followed by single word (eg: 1 Kings)
P.seq(pBookPrefixNumber, pAnySpace, P.letters).chain(x => {
    let name = x[0] + ' ' + x[2].toLowerCase();
    let id = book_name_to_id[name];
    if (id) {
        return P.succeed(id);
    }
    else {
        return P.fail("Invalid book name: " + x);
    }
}), 
// Single word book names
P.regexp(/[A-Z]+/i).chain(x => {
    let id = book_name_to_id[x.toLowerCase()];
    if (id) {
        return P.succeed(id);
    }
    else {
        return P.fail("Invalid book name: " + x);
    }
})).desc("Book name (eg, 'Genesis', '2 Kings')");
const pBook = P.alt(pBookName, pBookId).skip(P.regexp(/\.? */));
///////////////////////////////////////////////////////////////////////
// Parses a character that seperates a chapter number from verse number
const pVerseSeperator = P.oneOf(":v.").skip(pAnySpace);
// Parses a comma seperator, optionally followed by whitespace
const pCommaSeperator = P.oneOf(',').skip(pAnySpace);
const pRangeSeperator = P
    .optWhitespace
    .then(P.oneOf("-"))
    .skip(pAnySpace);
;
const pIntRange = P.seq(pInt, pRangeSeperator.then(pInt).fallback(null)).chain(([start, end]) => {
    if (end && end <= start) {
        return P.fail("End of range must be higher than start");
    }
    return P.succeed({ start, end });
});
const pChapterVerseSpecifier = P.alt(
// chapter_range:
P.seqMap(pInt.skip(pVerseSeperator), pInt, pRangeSeperator, pInt.skip(pVerseSeperator), pInt, (c1, v1, r, c2, v2) => { return { kind: "chapter_range", c1, v1, c2, v2 }; }), 
// Parses full chapters, eg "5", "5-8"
pIntRange.notFollowedBy(pVerseSeperator).map((range) => {
    return { kind: "full_chapter", range };
}), 
// Parses single chapter with verses, eg "5:8", "5:8-10",
P.seqMap(pInt, pVerseSeperator.then((pIntRange.notFollowedBy(pVerseSeperator)).sepBy1(pCommaSeperator)), (chapter, verses) => {
    return { kind: "verse", chapter, verses };
}));
// Converts a parsed ChapterVerseSpecifier into a list of BibleRefs
function chapterVerseSpecifierToBibleRef(book, cv) {
    switch (cv.kind) {
        case "full_chapter": {
            let start = cv.range.start;
            let end = cv.range.end ? cv.range.end : cv.range.start;
            let last_verse = 1;
            if (Versification_1.default.book[book][end] !== undefined) {
                last_verse = Versification_1.default.book[book][end].verse_count;
            }
            return [{
                    is_range: true,
                    start: { book, chapter: start, verse: 1 },
                    end: { book, chapter: end, verse: last_verse },
                }];
        }
        case "verse": {
            let chapter = cv.chapter;
            let results = [];
            for (let v of cv.verses) {
                if (v.end) {
                    results.push({
                        is_range: true,
                        start: { book, chapter, verse: v.start },
                        end: { book, chapter, verse: v.end },
                    });
                }
                else {
                    results.push({ book, chapter, verse: v.start });
                }
            }
            return results;
        }
        case "chapter_range":
            return [{
                    is_range: true,
                    start: { book, chapter: cv.c1, verse: cv.v1 },
                    end: { book, chapter: cv.c2, verse: cv.v2 },
                }];
    }
}
const pBibleRefSingle = P.alt(
// Ranges where both sides of - have a book name, for example:
//
// Eg:
// - Genesis 1:1 - Genesis 5:1
// - Genesis 1   - Genesis 5
// - Genesis - Exodus
P.seqMap(pBook, P.seq(pInt, pVerseSeperator.then(pInt).fallback(null)).fallback(null), pRangeSeperator, pBook, P.seq(pInt, pVerseSeperator.then(pInt).fallback(null)).fallback(null), (b1, b1_extra, r, b2, b2_extra) => {
    let start = { book: b1, chapter: 1, verse: 1 };
    if (b1_extra) {
        let [c1, v1] = b1_extra;
        start.chapter = c1;
        if (v1 !== null) {
            start.verse = v1;
        }
    }
    let end = {
        book: b2,
        chapter: Versification_1.default.book[b2].chapters.length,
        verse: 0
    };
    if (b2_extra) {
        let [c2, v2] = b2_extra;
        end.chapter = c2;
        if (v2 == null) {
            end.verse = Versification_1.default.book[b2][end.chapter].verse_count;
        }
        else {
            end.verse = v2;
        }
    }
    else {
        end.verse = Versification_1.default.book[b2][end.chapter].verse_count;
    }
    return [{ is_range: true, start, end }];
}), 
// Ranges within a single chapter
// EG: Gen 5:12-14
P.seqMap(pBook, pChapterVerseSpecifier.sepBy(pCommaSeperator), (book, cv_list) => {
    if (cv_list.length == 0) {
        // Then we just got a book name, no chapter/verse
        let max_chapter = Versification_1.default.book[book].chapters.length;
        let max_verse = Versification_1.default.book[book][max_chapter].verse_count;
        return [{
                is_range: true,
                start: { book, chapter: 1, verse: 1 },
                end: { book, chapter: max_chapter, verse: max_verse },
            }];
    }
    let results = [];
    for (let cv of cv_list) {
        results = results.concat(chapterVerseSpecifierToBibleRef(book, cv));
    }
    return results;
}));
const pBibleRef = pBibleRefSingle
    .sepBy1(pAnySpace.then(P.oneOf(';_')).then(pAnySpace))
    .map((list) => list.reduce((acc, x) => acc.concat(x), []));
;
const Parsers = {
    Book: pBook,
    BibleRef: pBibleRef,
};
exports.Parsers = Parsers;
exports.default = Parsers;
